import { z } from 'zod';
import type { Permission } from '../permissions';
import { emailSchema } from './common';

/**
 * No password-strength rule here: applying a strength policy at *sign-in*
 * rejects valid legacy passwords; strength belongs on create/change only.
 */
export const signInSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required'),
});
export type SignInInput = z.infer<typeof signInSchema>;

/**
 * Shape returned by POST /auth/signin and GET /auth/me, and attached to every
 * request by the guard. Never includes the token.
 *
 * `permissions` is the resolved set — role, plus allows, minus denies — read
 * from the database on every request, so a change an admin makes applies to
 * the next request rather than the next sign-in.
 */
export interface AuthenticatedUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string | null;
  role: { id: number; name: string; is_super: boolean };
  permissions: Permission[];
}

/**
 * JWT payload. Carries identity only: what the user may do is resolved from
 * the database per request, never trusted from a token minted before a change.
 * `sid` is the `UserSession` the token was minted for — the guard rejects the
 * token once that session has ended.
 */
export interface JwtPayload {
  sub: number;
  email: string;
  sid: number;
}

export const AUTH_COOKIE_NAME = 'ndc_token';
